import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { useOnline } from "@/lib/connectivity";
import { NeedsInternetNote } from "@/components/Offline";
import { ImageUpload } from "@/components/ImageUpload";
import { CostField } from "@/components/CostField";
import { useCan } from "@/context/AuthContext";

const emptyBatch = () => ({ batch_no: "", expiry: "", qty: "" });

export function EditProductDialog({ product, biz, branch, onClose, onSaved }) {
  const pharmacy = biz.business_type === "pharmacy";
  const online = useOnline();
  const [f, setF] = useState(null);
  const [batches, setBatches] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [cost, setCost] = useState(null);
  const can = useCan();
  const canCost = can("profit");

  useEffect(() => {
    if (product) {
      setF({ name: product.name, price: product.base_price, branch_price: product.branch_price || "", barcode: product.barcode || "", tax_rate: product.tax_rate || "", stock: product.stock == null ? "" : String(product.stock) });
      setBatches([]);
      setError("");
      setPhoto(product.photo_url || null);
      setCost(null);
      if (canCost) api.get(`/products/${product.id}/cost`).then((r) => setCost({ was: r.data.cost || "", v: r.data.cost || "" })).catch(() => {});
    }
  }, [product, canCost]);
  const [photo, setPhoto] = useState(null);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const setB = (i, k) => (e) => setBatches((bs) => bs.map((b, j) => (j === i ? { ...b, [k]: e.target.value } : b)));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { stock, ...rest } = f;
      const body = { ...rest };
      if (!pharmacy && stock.trim() !== "") {
        if (!/^-?\d+$/.test(stock.trim())) throw new Error("Stock must be a whole number");
        body.stock = parseInt(stock, 10);
      }
      let { data } = await api.put(`/products/${product.id}`, body);
      if (cost && cost.v.trim() !== cost.was) await api.put(`/products/${product.id}/cost`, { cost: cost.v.trim() });
      if (photo !== (product.photo_url || null)) data = { ...data, photo_url: (await api.put(`/store/products/${product.id}/photo`, { photo_url: photo })).data.photo_url };
      const nb = batches.filter((b) => b.batch_no.trim() || b.expiry || b.qty !== "");
      if (nb.length) {
        if (nb.some((b) => !b.batch_no.trim() || !b.expiry || b.qty === "")) throw new Error("Complete every batch row (batch no, expiry, qty)");
        data = (await api.post(`/products/${product.id}/batches`, { batches: nb.map((b) => ({ ...b, qty: parseInt(b.qty, 10) || 0 })) })).data;
      }
      toast.success(`${data.name} saved`);
      onSaved(data);
      onClose();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92dvh] max-w-lg overflow-y-auto" data-testid="edit-product-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Edit product</DialogTitle>
          <DialogDescription>Shared by all branches. Branch price and batches apply to {branch.name} only.</DialogDescription>
        </DialogHeader>
        {f && (
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-1.5"><Label>Name</Label><Input className="h-11" value={f.name} onChange={set("name")} data-testid="edit-product-name-input" /></div>
            <ImageUpload label="Photo (online store)" value={photo} onChange={setPhoto} client={api} testid="edit-product-photo" />
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Base price ({biz.currency})</Label><Input inputMode="decimal" className="money h-11" value={f.price} onChange={set("price")} data-testid="edit-product-price-input" /></div>
              <div className="space-y-1.5"><Label>{branch.name} price</Label><Input inputMode="decimal" className="money h-11" placeholder="Same as base" value={f.branch_price} onChange={set("branch_price")} data-testid="edit-product-branch-price-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Barcode</Label><Input className="money h-11" value={f.barcode} onChange={set("barcode")} data-testid="edit-product-barcode-input" /></div>
              <div className="space-y-1.5"><Label>Tax rate %</Label><Input inputMode="decimal" className="money h-11" placeholder={`${biz.tax_rate} (default)`} value={f.tax_rate} onChange={set("tax_rate")} data-testid="edit-product-tax-input" /></div>
            </div>
            {canCost && cost && <CostField biz={biz} value={cost.v} onChange={(v) => setCost((c) => ({ ...c, v }))} testid="edit-product-cost-input" />}
            {!pharmacy && (
              <div className="space-y-1.5"><Label>Stock on hand · {branch.name}</Label><Input inputMode="numeric" className="money h-11" placeholder="Not tracked" value={f.stock} onChange={set("stock")} data-testid="edit-product-stock-input" /></div>
            )}
            {pharmacy && (
              <div className="space-y-2 rounded-xl border border-amber-200 bg-amber-50/50 p-3" data-testid="edit-product-batches">
                <div className="flex items-center justify-between">
                  <span className="eyebrow !text-amber-800">Add batches · {branch.name}</span>
                  <button type="button" onClick={() => setBatches((b) => [...b, emptyBatch()])} className="flex h-9 items-center gap-1 rounded-lg px-2 text-sm font-semibold hover:bg-amber-100" data-testid="edit-product-add-batch-button"><Plus className="h-4 w-4" /> Batch</button>
                </div>
                {batches.map((b, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_64px_36px] items-center gap-2">
                    <Input placeholder="Batch no" className="h-10 font-mono text-sm" value={b.batch_no} onChange={setB(i, "batch_no")} data-testid={`edit-batch-no-${i}`} />
                    <Input type="date" className="h-10 text-sm" value={b.expiry} onChange={setB(i, "expiry")} data-testid={`edit-batch-expiry-${i}`} />
                    <Input type="number" min={0} placeholder="Qty" className="money h-10 text-sm" value={b.qty} onChange={setB(i, "qty")} data-testid={`edit-batch-qty-${i}`} />
                    <button type="button" onClick={() => setBatches((bs) => bs.filter((_, j) => j !== i))} className="grid h-9 w-9 place-items-center text-slate-400 hover:text-rose-600" aria-label="Remove"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            )}
            {error && <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm font-medium text-rose-700" data-testid="edit-product-error">{error}</div>}
            {!online && <NeedsInternetNote />}
            <button type="submit" disabled={busy || !online} data-testid="edit-product-save-button" className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-slate-900 font-semibold text-white disabled:bg-slate-300">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save changes
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
